import React from 'react';
import KeystoneLogo, { KeystoneMark } from '../components/KeystoneLogo';
import BrochureGate from '../components/BrochureGate';
import { downloadServerPDF } from '../lib/pdf-download';

/**
 * Business Plan brochure, prepared for the Digital Incubation Center,
 * Ministry of Communications and Information Technology, State of Qatar.
 *
 * Adapted from the visual source (`Keystone_Business_Plan_Visual_source.html`)
 * to the Keystone by CloudVoro brand identity used across the rest of the site:
 * navy / magenta / gold palette, Playfair Display display type, Inter body,
 * lot-numbered pages, PDF-safe A4 sections. Content preserved and lightly
 * edited only for name and brand consistency (Asif Khan, Keystone by CloudVoro).
 *
 * Route: `/brochure/bplan` — download via `downloadServerPDF()`.
 */

const TOTAL_PAGES = 15;

const C = {
  navy: '#0A2540',
  navyDeep: '#061A2E',
  magenta: '#E91E63',
  gold: '#C9A961',
  cream: '#FAF8F5',
  paper: '#FFFFFF',
  ink: '#0F1B2A',
  slate: '#3D4A5C',
  muted: '#6B7280',
  border: '#E2E8F0',
};

const Page = ({ children, bg = '#fff', last = false, pageNum, noPad = false }) => (
  <section
    className="brochure-page"
    data-page={pageNum}
    style={{
      width: '210mm',
      height: '297mm',
      background: bg,
      position: 'relative',
      overflow: 'hidden',
      pageBreakAfter: last ? 'auto' : 'always',
      breakAfter: last ? 'auto' : 'page',
      boxShadow: '0 8px 32px rgba(10, 37, 64, 0.12)',
      margin: '0 auto 24px',
      padding: noPad ? 0 : '14mm 16mm',
      boxSizing: 'border-box',
      fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      color: C.ink,
    }}
  >
    {children}
  </section>
);

const PageHeader = ({ pageNum, dark = false, lot }) => (
  <div
    style={{
      position: 'absolute',
      top: '8mm', left: '16mm', right: '16mm',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '8.5pt',
      color: dark ? 'rgba(255,255,255,0.65)' : C.muted,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      fontWeight: 600,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <KeystoneMark size={20} variant={dark ? 'dark' : 'light'} />
      <span style={{ color: dark ? '#fff' : C.navy, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'none', fontSize: '9.5pt' }}>Keystone</span>
      <span style={{ fontSize: '7.5pt' }}>·</span>
      <span style={{ color: dark ? C.gold : C.magenta, fontSize: '7.5pt', letterSpacing: '0.15em' }}>BY CLOUDVORO</span>
    </div>
    <span>{lot ? `${lot} · ` : ''}Business plan · {pageNum} / {TOTAL_PAGES}</span>
  </div>
);

const PageFooter = ({ dark = false, section }) => (
  <div
    style={{
      position: 'absolute',
      bottom: '8mm', left: '16mm', right: '16mm',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '7.5pt',
      color: dark ? 'rgba(255,255,255,0.55)' : C.muted,
      letterSpacing: '0.06em',
    }}
  >
    <span>Keystone by CloudVoro{section ? ` · ${section}` : ''}</span>
    <span>Confidential · Prepared for the Digital Incubation Center, Doha</span>
  </div>
);

const LotBadge = ({ n, label }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: '4mm' }}>
    <span style={{ fontSize: '9pt', letterSpacing: '0.28em', textTransform: 'uppercase', fontWeight: 700, color: C.magenta }}>
      Section {n}
    </span>
    <span style={{ fontSize: '8pt', letterSpacing: '0.2em', color: C.muted, fontWeight: 600 }}>·</span>
    <span style={{ fontSize: '8pt', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 600, color: C.muted }}>
      {label}
    </span>
  </div>
);

const H2 = ({ children, style = {} }) => (
  <h2 style={{
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '26pt', fontWeight: 700, color: C.navy,
    lineHeight: 1.12, letterSpacing: '-0.018em', margin: '0 0 5mm',
    ...style,
  }}>
    {children}
  </h2>
);

const Body = ({ children, style = {} }) => (
  <p style={{ fontSize: '10.5pt', color: C.slate, lineHeight: 1.65, margin: '0 0 4mm', ...style }}>
    {children}
  </p>
);

// ==================== PAGE 1: Cover ====================

const CoverPage = () => (
  <Page pageNum={1} bg={C.navy} noPad>
    <div style={{ padding: '14mm 16mm', height: '100%', display: 'flex', flexDirection: 'column', color: '#fff', position: 'relative' }}>
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: '42%',
        backgroundImage: "url('/press/cheese-maturation.jpg')",
        backgroundSize: 'cover', backgroundPosition: 'center',
        opacity: 0.22,
      }} aria-hidden="true" />
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: '58%',
        background: `linear-gradient(90deg, ${C.navy} 30%, transparent 100%)`,
      }} aria-hidden="true" />

      <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '8.5pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <KeystoneMark size={22} variant="dark" />
          <span style={{ color: '#fff', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'none', fontSize: '10pt' }}>Keystone</span>
          <span style={{ fontSize: '8pt' }}>·</span>
          <span style={{ color: C.gold, fontSize: '8pt', letterSpacing: '0.18em' }}>BY CLOUDVORO</span>
        </div>
        <span>Doha · 2026 &nbsp;|&nbsp; Business plan · confidential</span>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop: '10mm', position: 'relative', zIndex: 2 }}>
        <div style={{ marginBottom: '10mm' }}>
          <KeystoneLogo variant="dark" size={44} />
        </div>
        <div style={{ fontSize: '10pt', letterSpacing: '0.3em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '8mm' }}>
          Business plan · prepared for the Digital Incubation Center
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: '50pt', fontWeight: 700, color: '#fff',
          lineHeight: 1.03, letterSpacing: '-0.024em', margin: 0, maxWidth: '160mm',
        }}>
          Food safety is<br/>
          <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>not a spreadsheet.</em>
        </h1>
        <div style={{ marginTop: '10mm', fontSize: '13pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.55, maxWidth: '150mm', fontWeight: 400 }}>
          The operating system for small and mid-sized food manufacturers. Live in production. Built in Ireland. Scaling from Qatar, across the Gulf and beyond.
        </div>

        <div style={{
          marginTop: '14mm', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8mm',
          padding: '6mm 7mm', background: 'rgba(255,255,255,0.06)', borderLeft: `3px solid ${C.gold}`,
          maxWidth: '160mm',
        }}>
          <div>
            <div style={{ fontSize: '8.5pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '2mm' }}>
              Prepared for
            </div>
            <div style={{ fontSize: '10.5pt', lineHeight: 1.55, color: 'rgba(255,255,255,0.92)' }}>
              Digital Incubation Center<br/>
              Ministry of Communications and Information Technology<br/>
              State of Qatar
            </div>
          </div>
          <div>
            <div style={{ fontSize: '8.5pt', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '2mm' }}>
              Founder
            </div>
            <div style={{ fontSize: '10.5pt', lineHeight: 1.55, color: 'rgba(255,255,255,0.92)' }}>
              Mohammed Asif Ali Khan<br/>
              asif@cloudvoro.com<br/>
              keystone.cloudvoro.com
            </div>
          </div>
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '8pt', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.06em', paddingTop: '6mm', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
        <span>keystone.cloudvoro.com</span>
        <span>Cloudvoro Ltd · Thurles, Co. Tipperary, Ireland · CRO 795787</span>
      </div>
    </div>
  </Page>
);

// ==================== PAGE 2: Why we exist ====================

const WhyPage = () => (
  <Page pageNum={2} bg={C.cream}>
    <PageHeader pageNum={2} lot="Section 01 · Why" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="01" label="Why we exist" />
      <H2>
        Every batch of food a country eats carries a record.<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>Most of them are written on paper.</em>
      </H2>

      <Body>
        We believe a producer&rsquo;s size should never determine how safely it can feed people. The multinational has a system. The producer employing forty people has a ring binder, a spreadsheet and a printer that jams.
      </Body>
      <Body>
        Keystone exists to close that gap. Not by selling smaller companies a smaller ERP, but by building the system food manufacturers would have designed for themselves and putting it within their reach.
      </Body>

      <div style={{ marginTop: '10mm', padding: '8mm 9mm', background: C.navy, color: '#fff', position: 'relative' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.24em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '4mm' }}>
          The question every producer eventually faces
        </div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22pt', fontWeight: 700, lineHeight: 1.2, color: '#fff', letterSpacing: '-0.012em' }}>
          A recall does not ask how big you are.<br/>
          <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>It asks one question: can you prove it?</em>
        </div>
      </div>

      <div style={{ marginTop: '10mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          { k: 'Obligation', v: 'A single hygiene, traceability and audit standard for every producer, from a co-op of six to a group of six thousand.' },
          { k: 'Reality', v: 'The infrastructure to meet that standard has only ever been priced for the largest producers in the market.' },
          { k: 'Consequence', v: 'The producers doing the most agile, most local, most interesting food are the ones carrying the most operational risk.' },
        ].map((c, i) => (
          <div key={i} style={{ background: C.paper, borderTop: `3px solid ${C.magenta}`, border: `1px solid ${C.border}`, padding: '5mm 5mm' }}>
            <div style={{ fontSize: '8.5pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.magenta, fontWeight: 700, marginBottom: '2mm' }}>{c.k}</div>
            <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.55 }}>{c.v}</div>
          </div>
        ))}
      </div>
    </div>
    <PageFooter section="Why · How · What" />
  </Page>
);

// ==================== PAGE 3: The three questions ====================

const PositionPage = () => (
  <Page pageNum={3} bg={C.paper}>
    <PageHeader pageNum={3} lot="Section 02 · Position" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="02" label="The three questions" />
      <H2>
        Why. <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>How.</em> What.
      </H2>

      {/* Three-column stack */}
      <div style={{ marginTop: '4mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          {
            tag: 'WHY',
            head: 'Proof should not be a privilege',
            body: 'Traceability, food safety and retailer compliance are obligations for every producer. The systems that deliver them have only ever been affordable to a few.',
          },
          {
            tag: 'HOW',
            head: 'Build it inside a working factory',
            body: 'Keystone was not specified in a workshop. Every screen exists because a production, quality or dispatch team needed it during a live shift in a plant exporting to international retailers.',
          },
          {
            tag: 'WHAT',
            head: 'One platform, weeks not years',
            body: 'Production, batch traceability, dispatch documentation, pallet labelling, quality records and audit history, deployed in weeks, at a subscription an SME approves without a board paper.',
          },
        ].map((c, i) => (
          <div key={i} style={{ background: C.cream, borderTop: `4px solid ${C.magenta}`, padding: '6mm 6mm', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '4mm', right: '5mm', fontFamily: "'Playfair Display', Georgia, serif", fontSize: '28pt', color: 'rgba(233,30,99,0.14)', fontWeight: 700, lineHeight: 1 }}>{c.tag}</div>
            <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.magenta, fontWeight: 700, marginBottom: '3mm', position: 'relative', zIndex: 2 }}>{c.tag}</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '14pt', color: C.navy, fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em', marginBottom: '3mm', position: 'relative', zIndex: 2 }}>{c.head}</div>
            <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.55, position: 'relative', zIndex: 2 }}>{c.body}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '12mm', padding: '7mm 8mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '18pt', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.012em' }}>
          Most software companies sell what they built. Keystone starts from <em style={{ color: C.gold, fontWeight: 500 }}>why</em> it was built:
        </div>
        <div style={{ marginTop: '4mm', fontSize: '11pt', color: 'rgba(255,255,255,0.88)', lineHeight: 1.6, maxWidth: '160mm' }}>
          so that a forty-person creamery can answer a regulator, a retailer and a recall with the same confidence as a multinational, on the same working day.
        </div>
      </div>
    </div>
    <PageFooter section="Positioning" />
  </Page>
);

// ==================== PAGE 4: The problem ====================

const ProblemPage = () => (
  <Page pageNum={4} bg={C.cream}>
    <PageHeader pageNum={4} lot="Section 03 · Problem" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="03" label="The problem" />
      <H2>
        Two bad choices, and a<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>filing cabinet.</em>
      </H2>
      <Body>
        Small and mid-sized food manufacturers carry the same obligations as multinationals and none of the infrastructure. The software market has offered them a system built for someone ten times their size, or a system that has never seen a food factory. Most choose neither, and stay on paper.
      </Body>

      {/* Positioning quadrant SVG */}
      <div style={{ marginTop: '6mm', background: C.paper, border: `1px solid ${C.border}`, padding: '7mm 7mm' }}>
        <svg viewBox="0 0 760 360" width="100%" height="215" style={{ display: 'block' }} preserveAspectRatio="xMidYMid meet">
          {/* Axes */}
          <line x1="70" y1="24" x2="70" y2="320" stroke={C.slate} strokeWidth="1.4" />
          <line x1="70" y1="320" x2="740" y2="320" stroke={C.slate} strokeWidth="1.4" />
          <text x="70" y="16" fontSize="13" fill={C.muted} fontWeight="700" letterSpacing="2" textAnchor="start">CAPABILITY</text>
          <text x="740" y="345" fontSize="13" fill={C.muted} fontWeight="700" letterSpacing="2" textAnchor="end">COST</text>

          {/* Paper bottom-left */}
          <circle cx="165" cy="250" r="20" fill={C.slate} opacity="0.85" />
          <text x="165" y="294" fontSize="15" fill={C.navy} fontWeight="700" textAnchor="middle" letterSpacing="0.5">PAPER</text>
          <text x="165" y="312" fontSize="12" fill={C.muted} textAnchor="middle">the real incumbent</text>

          {/* Generic MRP middle */}
          <circle cx="375" cy="205" r="20" fill={C.muted} />
          <text x="375" y="249" fontSize="15" fill={C.navy} fontWeight="700" textAnchor="middle" letterSpacing="0.5">GENERIC MRP</text>
          <text x="375" y="267" fontSize="12" fill={C.muted} textAnchor="middle">not built for food</text>

          {/* Tier-1 ERP top-right */}
          <circle cx="620" cy="105" r="26" fill={C.navy} />
          <text x="620" y="155" fontSize="15" fill={C.navy} fontWeight="700" textAnchor="middle" letterSpacing="0.5">TIER-ONE ERP</text>
          <text x="620" y="173" fontSize="12" fill={C.muted} textAnchor="middle">months, consultants, capex</text>

          {/* Keystone sweet spot */}
          <circle cx="250" cy="105" r="30" fill={C.magenta} stroke={C.gold} strokeWidth="3" />
          <text x="250" y="160" fontSize="16" fill={C.magenta} fontWeight="700" textAnchor="middle" letterSpacing="0.5">KEYSTONE</text>
          <text x="250" y="178" fontSize="12" fill={C.navy} textAnchor="middle">food-specific depth</text>
          <text x="250" y="195" fontSize="12" fill={C.magenta} fontWeight="700" textAnchor="middle">SME price · weeks to deploy</text>
        </svg>
      </div>

      <div style={{ marginTop: '8mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          { h: 'What paper costs', b: 'Traceability exercises measured in hours. Dispatch paperwork rebuilt by hand for every retailer template. Label errors. Lost batch records. Audit weeks.' },
          { h: 'Why ERP does not fit', b: 'Priced and staffed for companies with an IT department. Implementation timelines that outlast the problem. Configuration that requires a consultant on retainer.' },
          { h: 'Why generic tools miss', b: 'No retailer-template dispatch documents. No GS1-128 or SSCC pallet labelling. No certificate of conformance or analysis workflow. No food-grade lot depth.' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '5mm 5mm', background: C.paper, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.magenta}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '13pt', color: C.navy, fontWeight: 700, marginBottom: '3mm', letterSpacing: '-0.01em' }}>{c.h}</div>
            <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.55 }}>{c.b}</div>
          </div>
        ))}
      </div>
    </div>
    <PageFooter section="Problem" />
  </Page>
);

// ==================== PAGE 5: Qatar ====================

const QatarPage = () => (
  <Page pageNum={5} bg={C.paper}>
    <PageHeader pageNum={5} lot="Section 04 · Qatar" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="04" label="Why Qatar, why now" />
      <H2>
        Qatar decided to make more<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>of its own food.</em>
      </H2>
      <Body>
        The National Food Security Strategy 2030, launched in December 2024, runs seventeen initiatives across local production, strategic reserves and trade partnerships, with explicit national targets for food safety, waste reduction and private sector delivery. Every producer it creates or expands needs an operational system on day one.
      </Body>

      {/* Three big number blocks */}
      <div style={{ marginTop: '8mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6mm' }}>
        {[
          { v: '98%', k: 'Self-sufficient in fresh dairy and poultry', s: 'An industrialised producer base already in place, moving up the compliance curve.' },
          { v: '2030', k: 'National targets rising', s: 'Vegetables, eggs, red meat and fish, with food waste and food loss targeted for deep cuts.' },
          { v: '17', k: 'Strategic initiatives', s: 'Private sector named as central to delivery. Policy-backed demand, not speculative.' },
        ].map((m, i) => (
          <div key={i} style={{ padding: '6mm 5mm', background: C.cream, borderLeft: `4px solid ${C.gold}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '38pt', fontWeight: 700, color: C.navy, lineHeight: 1, letterSpacing: '-0.03em' }}>{m.v}</div>
            <div style={{ fontSize: '10pt', color: C.navy, marginTop: '3mm', fontWeight: 700, letterSpacing: '-0.005em', lineHeight: 1.35 }}>{m.k}</div>
            <div style={{ fontSize: '9pt', color: C.slate, marginTop: '2mm', lineHeight: 1.55 }}>{m.s}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '10mm', padding: '7mm 8mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
          The strategic bet
        </div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17pt', fontWeight: 700, lineHeight: 1.3, color: '#fff', letterSpacing: '-0.01em' }}>
          Qatar&rsquo;s producers face the same two bad choices as Ireland&rsquo;s. The difference is that here, <em style={{ color: C.gold, fontWeight: 500 }}>solving it is national policy.</em>
        </div>
        <div style={{ marginTop: '4mm', fontSize: '10pt', color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
          Keystone intends to be the answer built from Qatar, and then to sell that answer to every other food producer in the Gulf.
        </div>
      </div>
    </div>
    <PageFooter section="Market" />
  </Page>
);

// ==================== PAGE 6: Product ====================

const ProductPage = () => (
  <Page pageNum={6} bg={C.cream}>
    <PageHeader pageNum={6} lot="Section 05 · Product" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="05" label="The product" />
      <H2>
        From intake to pallet,<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>on one record.</em>
      </H2>

      {/* Trace chain diagram */}
      <div style={{ marginTop: '6mm', padding: '7mm 6mm', background: C.paper, border: `1px solid ${C.border}` }}>
        <svg viewBox="0 0 780 130" width="100%" height="70" preserveAspectRatio="xMidYMid meet">
          <line x1="60" y1="65" x2="720" y2="65" stroke={C.magenta} strokeWidth="2" strokeDasharray="6 4" opacity="0.6" />
          {[
            { x: 60, l: 'INTAKE', s: 'ingredient lots' },
            { x: 225, l: 'PRODUCTION', s: 'batches, yields' },
            { x: 390, l: 'QUALITY', s: 'CoC / CoA' },
            { x: 555, l: 'DISPATCH', s: 'docs, SSCC labels' },
            { x: 720, l: 'AUDIT', s: 'full history' },
          ].map((n, i) => (
            <g key={i}>
              <circle cx={n.x} cy="65" r="18" fill={C.navy} />
              <circle cx={n.x} cy="65" r="7" fill={C.gold} />
              <text x={n.x} y="30" fontSize="12" fill={C.navy} fontWeight="700" textAnchor="middle" letterSpacing="1.4">{n.l}</text>
              <text x={n.x} y="106" fontSize="9.5" fill={C.muted} textAnchor="middle">{n.s}</text>
            </g>
          ))}
        </svg>
        <div style={{ textAlign: 'center', marginTop: '3mm', fontSize: '9pt', letterSpacing: '0.24em', color: C.magenta, fontWeight: 700, textTransform: 'uppercase' }}>
          One unbroken traceability chain
        </div>
      </div>

      <div style={{ marginTop: '8mm', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '5mm' }}>
        {[
          { h: 'Operations', b: 'Production and batch management. Ingredient and finished-product traceability. Batch and lot tracking. Stock movement and returns. Event and sample stock reconciliation. Operational reporting.' },
          { h: 'Compliance', b: 'Quality and compliance records. Certificate of conformance and certificate of analysis workflows. Role-based permissions. Complete audit history.' },
          { h: 'Dispatch, the wedge', b: 'Customer-specific product and delivery requirements. Retailer-template dispatch documentation. GS1-128 and SSCC pallet and product labels. Customer and shipping data. This is the daily pain no generic tool touches.', accent: true },
          { h: 'Platform', b: 'React, FastAPI, MongoDB, AWS. REST integrations to accounting, weighing and labelling systems. Automated document and PDF generation. Modular by design, so regional capability is added, never rebuilt.' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '5mm 5mm', background: C.paper, border: `1px solid ${C.border}`, borderLeft: `4px solid ${c.accent ? C.magenta : C.gold}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '13pt', color: C.navy, fontWeight: 700, marginBottom: '3mm', letterSpacing: '-0.01em' }}>
              {c.h}
            </div>
            <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.6 }}>{c.b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '6mm', padding: '5mm 6mm', background: `rgba(201,169,97,0.14)`, borderLeft: `3px solid ${C.gold}` }}>
        <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.6 }}>
          <strong style={{ color: C.navy }}>The architecture is the strategy.</strong> Because modules attach to a single core, Arabic documentation, halal certification workflows, GSO labelling and ZATCA e-invoicing become additions rather than forks, which is what makes a lean team credible across six countries.
        </div>
      </div>
    </div>
    <PageFooter section="Product" />
  </Page>
);

// ==================== PAGE 7: Proof ====================

const ProofPage = () => (
  <Page pageNum={7} bg={C.paper}>
    <PageHeader pageNum={7} lot="Section 06 · Proof" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="06" label="Proof" />
      <H2>
        One customer.<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>Every single working day.</em>
      </H2>
      <Body>
        Keystone runs the daily operations of an established, internationally exporting food manufacturer. Real batches, real audits, real retailer dispatch paperwork, since early 2026.
      </Body>
      <Body>
        We report that as <strong style={{ color: C.navy }}>one customer</strong>, deliberately. Other platforms the founder has built are not Keystone customers and are not counted here. A single manufacturer trusting a system with its production, its traceability and its retailer relationships is worth more than a list.
      </Body>
      <Body style={{ fontStyle: 'italic', color: C.navy }}>
        The product roadmap is set by that factory. Features exist because a shift needed them.
      </Body>

      <div style={{ marginTop: '8mm', padding: '6mm 6mm', background: C.cream, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.24em', textTransform: 'uppercase', color: C.magenta, fontWeight: 700, marginBottom: '4mm' }}>
          Founder build record
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4mm 6mm' }}>
          {[
            { h: 'Ballypatrick Auctions', b: 'Online auction platform supporting €5M+ in annual horse sales.' },
            { h: 'Boomerang ProductHub', b: 'Order orchestration across Akeneo, ARANA API and Shopify.' },
            { h: 'Shaku Maku Catering', b: 'Catering CRM with Stripe payments and customer journey analytics.' },
            { h: 'Ballypatrick Stallions CRM', b: 'Breeding, bookings and operations management.' },
          ].map((c, i) => (
            <div key={i} style={{ paddingLeft: '4mm', borderLeft: `3px solid ${C.gold}` }}>
              <div style={{ fontSize: '11pt', fontWeight: 700, color: C.navy, letterSpacing: '-0.01em' }}>{c.h}</div>
              <div style={{ fontSize: '9.5pt', color: C.slate, marginTop: '1mm', lineHeight: 1.55 }}>{c.b}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '5mm', paddingTop: '4mm', borderTop: `1px solid ${C.border}`, fontSize: '10pt', color: C.navy, fontWeight: 700, letterSpacing: '-0.005em' }}>
          Four industries. Four working systems. Keystone is not a first attempt.
        </div>
      </div>

        <div style={{ marginTop: '8mm', padding: '6mm 7mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
          Regional history
        </div>
        <div style={{ fontSize: '11pt', lineHeight: 1.65, color: 'rgba(255,255,255,0.9)' }}>
          Before Ireland: a decade of enterprise delivery in Saudi Arabia, direct and subcontracted, supporting government, defence, diplomatic and banking organisations, alongside ZATCA electronic invoicing implementations on Microsoft AX and SAP. The GCC is not a new market for this founder. It is a return to one.
        </div>
      </div>
    </div>
    <PageFooter section="Validation" />
  </Page>
);

// ==================== PAGE 8: The Qatar base ====================

const QatarBasePage = () => (
  <Page pageNum={8} bg={C.cream}>
    <PageHeader pageNum={8} lot="Section 07 · The base" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="07" label="The Qatar base" />
      <H2>
        Most companies come to the Gulf to sell software.<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>We want to build our business here.</em>
      </H2>
      <Body>
        A wholly owned Qatar entity will be incorporated during year one as Keystone&rsquo;s regional product, commercial and development base. The Arabic and GCC capability is built there and held there. The group&rsquo;s long-term corporate and IP structure follows validation, not the other way round.
      </Body>

      {/* Diagram */}
      <div style={{ marginTop: '6mm', padding: '6mm 5mm', background: C.paper, border: `1px solid ${C.border}` }}>
        <svg viewBox="0 0 780 210" width="100%" height="120" preserveAspectRatio="xMidYMid meet">
          {/* central Doha block */}
          <rect x="270" y="70" width="240" height="70" fill={C.magenta} rx="4" />
          <text x="390" y="98" fontSize="13" fill="#fff" fontWeight="700" textAnchor="middle" letterSpacing="1">KEYSTONE QATAR</text>
          <text x="390" y="118" fontSize="9" fill="#fff" textAnchor="middle" letterSpacing="1.5">QSTP FREE ZONE · REGIONAL PRODUCT BASE</text>

          {/* Ireland/UK */}
          <rect x="30" y="30" width="200" height="46" fill={C.navy} rx="4" />
          <text x="130" y="52" fontSize="11" fill="#fff" fontWeight="700" textAnchor="middle">IRELAND & UK</text>
          <text x="130" y="68" fontSize="8.5" fill={C.gold} textAnchor="middle" letterSpacing="1">PROVEN BASE</text>

          {/* Qatar market */}
          <rect x="30" y="135" width="200" height="46" fill={C.navy} rx="4" />
          <text x="130" y="157" fontSize="11" fill="#fff" fontWeight="700" textAnchor="middle">QATAR MARKET</text>
          <text x="130" y="173" fontSize="8.5" fill={C.gold} textAnchor="middle" letterSpacing="1">DIRECT SALES</text>

          {/* Gulf export */}
          <rect x="550" y="80" width="200" height="46" fill={C.navy} rx="4" />
          <text x="650" y="102" fontSize="11" fill="#fff" fontWeight="700" textAnchor="middle">KSA · UAE · GCC</text>
          <text x="650" y="118" fontSize="8.5" fill={C.gold} textAnchor="middle" letterSpacing="1">EXPORT, PARTNER-LED</text>

          {/* Arrows */}
          <line x1="235" y1="55" x2="270" y2="88" stroke={C.slate} strokeWidth="1.5" markerEnd="url(#arr)" />
          <line x1="235" y1="155" x2="270" y2="122" stroke={C.slate} strokeWidth="1.5" markerEnd="url(#arr)" />
          <line x1="510" y1="105" x2="550" y2="105" stroke={C.slate} strokeWidth="1.5" markerEnd="url(#arr)" />
          <defs>
            <marker id="arr" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0, 8 4, 0 8" fill={C.slate} /></marker>
          </defs>
        </svg>
        <div style={{ textAlign: 'center', marginTop: '4mm', fontSize: '9pt', letterSpacing: '0.22em', color: C.magenta, fontWeight: 700, textTransform: 'uppercase' }}>
          One platform · three markets · one regional base
        </div>
      </div>

      <div style={{ marginTop: '8mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          { h: 'Built in Doha', b: 'Arabic and bilingual documentation, halal workflows, GSO labelling, ZATCA e-invoicing and the regional AI layer are developed from Doha and held by the Qatar entity, with IP assignment written into every development contract.' },
          { h: 'Existing platform', b: 'The core platform stays with the established European structure through validation. Consolidating it under the Qatar entity is an option we intend to evaluate on evidence, with professional legal and tax advice in both jurisdictions.' },
          { h: 'Protection', b: 'Keystone and CloudVoro marks registered in Qatar first, then KSA, UAE and the wider GCC. Source custody, contributor assignment and escrow for enterprise buyers.' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '5mm 5mm', background: C.paper, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.gold}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '12.5pt', color: C.navy, fontWeight: 700, marginBottom: '3mm', letterSpacing: '-0.01em' }}>{c.h}</div>
            <div style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.55 }}>{c.b}</div>
          </div>
        ))}
      </div>
    </div>
    <PageFooter section="Qatar base" />
  </Page>
);

// ==================== PAGE 9: Export strategy ====================

const ExportPage = () => (
  <Page pageNum={9} bg={C.paper}>
    <PageHeader pageNum={9} lot="Section 08 · Export" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="08" label="Export strategy" />
      <H2>
        Doha sells in <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>three directions.</em>
      </H2>

      <div style={{ marginTop: '4mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          { d: '01', tag: 'HOME', h: 'Qatar', b: 'Dairy, poultry, meat processing, aquaculture, bakery and prepared foods, reached through the DIC ecosystem and direct founder-led sales. Target: one flagship national reference.' },
          { d: '02', tag: 'OUTWARD', h: 'The Gulf', b: 'Saudi Arabia first, the largest food market in the GCC and the one where the founder\u2019s delivery history and ZATCA experience are strongest. Then UAE and Kuwait, then Oman and Bahrain.' },
          { d: '03', tag: 'INWARD', h: 'Ireland & UK', b: 'An existing, validated pipeline of artisan and SME producers supplying Irish and UK multiples. A proven base that funds and de-risks Gulf entry rather than competing with it.' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '6mm 5mm', background: C.cream, borderTop: `4px solid ${C.magenta}` }}>
            <div style={{ fontSize: '8pt', letterSpacing: '0.22em', color: C.muted, fontWeight: 700, marginBottom: '2mm' }}>DIRECTION {c.d} · {c.tag}</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17pt', color: C.navy, fontWeight: 700, letterSpacing: '-0.015em', marginBottom: '3mm' }}>{c.h}</div>
            <div style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.55 }}>{c.b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '9mm' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.magenta, fontWeight: 700, marginBottom: '3mm' }}>Localisation roadmap</div>
        <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.55, marginBottom: '4mm' }}>
          Each capability below is a module on the existing core, each is built from Doha and held by the Qatar entity, and together they are a combination we have not found well served by SME-priced platforms in the GCC.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt', color: C.slate }}>
          <thead>
            <tr style={{ background: C.navy, color: '#fff' }}>
              <th style={{ padding: '3mm 4mm', textAlign: 'left', fontWeight: 700, letterSpacing: '0.1em', fontSize: '8.5pt', textTransform: 'uppercase' }}>Capability</th>
              <th style={{ padding: '3mm 4mm', textAlign: 'left', fontWeight: 700, letterSpacing: '0.1em', fontSize: '8.5pt', textTransform: 'uppercase' }}>Why it wins the region</th>
              <th style={{ padding: '3mm 4mm', textAlign: 'left', fontWeight: 700, letterSpacing: '0.1em', fontSize: '8.5pt', textTransform: 'uppercase' }}>Market</th>
              <th style={{ padding: '3mm 4mm', textAlign: 'left', fontWeight: 700, letterSpacing: '0.1em', fontSize: '8.5pt', textTransform: 'uppercase' }}>Timing</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Arabic and bilingual UI, labels, documents', 'Arabic-English production records and dispatch paperwork', 'All GCC', 'Year 1'],
              ['Halal certification workflows', 'Certification records and batch-level halal status', 'All GCC', 'Year 1–2'],
              ['GSO labelling standards', 'Labelling and date-marking to Gulf standards', 'All GCC', 'Year 1–2'],
              ['Regional retailer dispatch templates', 'The wedge, rebuilt for Gulf retail and foodservice', 'QA · KSA · UAE', 'Year 1–2'],
              ['ZATCA e-invoicing integration', 'Compliant invoicing, built by someone who has done it before', 'KSA', 'Year 2'],
              ['AI-assisted operations modules', 'Assisted documentation, anomaly detection, plain-language reporting', 'Global', 'Year 2+'],
            ].map((r, i) => (
              <tr key={i} style={{ background: i % 2 ? C.cream : '#fff', borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '3mm 4mm', fontWeight: 600, color: C.navy }}>{r[0]}</td>
                <td style={{ padding: '3mm 4mm' }}>{r[1]}</td>
                <td style={{ padding: '3mm 4mm' }}>{r[2]}</td>
                <td style={{ padding: '3mm 4mm', fontWeight: 700, color: C.magenta }}>{r[3]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '6mm', padding: '4mm 6mm', background: `rgba(201,169,97,0.14)`, borderLeft: `3px solid ${C.gold}`, fontSize: '9pt', color: C.slate, lineHeight: 1.55 }}>
        <strong style={{ color: C.navy }}>Why this sequence.</strong> Saudi Arabia is roughly half the GCC food market and the market the founder knows best. Winning Qatar first makes the Saudi conversation a reference call rather than a cold introduction.
      </div>
    </div>
    <PageFooter section="Export" />
  </Page>
);

// ==================== PAGE 10: Business model ====================

const ModelPage = () => (
  <Page pageNum={10} bg={C.cream}>
    <PageHeader pageNum={10} lot="Section 09 · Model" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="09" label="Business model" />
      <H2>
        Recurring revenue,<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>bought out of operating budget.</em>
      </H2>
      <Body>
        Subscription per manufacturing site, plus a one-off implementation. Priced so the decision sits with the operations director, not the board, which is why the sales cycle is measured in weeks and the customer stays for years.
      </Body>

      <table style={{ marginTop: '6mm', width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt', color: C.slate }}>
        <thead>
          <tr style={{ background: C.navy, color: '#fff' }}>
            <th style={{ padding: '3.5mm 4mm', textAlign: 'left', fontWeight: 700, letterSpacing: '0.1em', fontSize: '8.5pt', textTransform: 'uppercase' }}>Component</th>
            <th style={{ padding: '3.5mm 4mm', textAlign: 'left', fontWeight: 700, letterSpacing: '0.1em', fontSize: '8.5pt', textTransform: 'uppercase' }}>Indicative price (QAR)</th>
            <th style={{ padding: '3.5mm 4mm', textAlign: 'left', fontWeight: 700, letterSpacing: '0.1em', fontSize: '8.5pt', textTransform: 'uppercase' }}>Basis</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Implementation and configuration', '20,000 – 45,000', 'One-off, scoped by site complexity'],
            ['Platform subscription', '2,000 – 5,000 / month', 'Per site, by modules and users'],
            ['Additional modules', '400 – 900 / month', 'Per module, per site'],
            ['Integrations and custom workflows', 'Scoped', 'Fixed-price professional services'],
            ['Training and premium support', 'Scoped', 'Optional annual packages'],
          ].map((r, i) => (
            <tr key={i} style={{ background: i % 2 ? '#fff' : C.paper, borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: '3.5mm 4mm', fontWeight: 600, color: C.navy }}>{r[0]}</td>
              <td style={{ padding: '3.5mm 4mm', fontWeight: 700, color: C.magenta }}>{r[1]}</td>
              <td style={{ padding: '3.5mm 4mm' }}>{r[2]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: '8mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm' }}>
        {[
          { v: 'Weeks', l: 'To deploy, against the months or years an ERP implementation consumes' },
          { v: 'Low', l: 'Churn by design. A system embedded in daily production is not switched casually' },
          { v: 'Modular', l: 'Revenue per site rises with each module, without proportionate cost to serve' },
        ].map((m, i) => (
          <div key={i} style={{ padding: '5mm 5mm', background: C.paper, borderLeft: `4px solid ${C.gold}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '26pt', color: C.navy, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>{m.v}</div>
            <div style={{ fontSize: '9pt', color: C.slate, marginTop: '3mm', lineHeight: 1.5 }}>{m.l}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '7mm', padding: '5mm 6mm', background: C.navy, color: 'rgba(255,255,255,0.9)', fontSize: '10pt', lineHeight: 1.6 }}>
        The model deliberately favours a smaller number of deeply embedded customers over high-volume, low-touch sales. Regional pricing for Qatar and the GCC will be validated in the first phase of incubation.
      </div>
    </div>
    <PageFooter section="Model" />
  </Page>
);

// ==================== PAGE 11: 12 + 12 month plan ====================

const PlanPage = () => (
  <Page pageNum={11} bg={C.paper}>
    <PageHeader pageNum={11} lot="Section 10 · Plan" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="10" label="Twelve months, then twelve more" />
      <H2>
        One reference customer<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>beats ten logos.</em>
      </H2>
      <Body>
        For an operational platform, a single Qatari manufacturer running Keystone through every shift is worth more than a page of pilots. The incubation year builds a repeatable model, not a vanity number.
      </Body>

      {/* Three phase blocks */}
      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4mm' }}>
        {[
          { m: 'Months 1–3', tag: 'Validate', b: 'Priority segments, producer and stakeholder discussions, Qatar traceability and compliance requirements, GCC materials, localisation scoping.', d: 'Market validation report · qualified pipeline' },
          { m: 'Months 4–6', tag: 'Pilot', b: 'Demonstrations with shortlisted manufacturers, one Qatar or GCC pilot secured, platform configured regionally. Qatar entity incorporation begins.', d: 'First GCC pilot · registration underway' },
          { m: 'Months 7–9', tag: 'Commercialise', b: 'Pilot converted, GCC implementation methodology, regional pricing and support. Long-term corporate and IP structure evaluated with advisers.', d: 'First commercial customer · structure decided on evidence' },
        ].map((p, i) => (
          <div key={i} style={{ padding: '5mm 5mm', background: C.cream, borderTop: `4px solid ${C.magenta}` }}>
            <div style={{ fontSize: '8.5pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.muted, fontWeight: 700 }}>{p.m}</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '18pt', fontWeight: 700, color: C.navy, letterSpacing: '-0.015em', margin: '2mm 0 3mm' }}>{p.tag}</div>
            <div style={{ fontSize: '9pt', color: C.slate, lineHeight: 1.55, marginBottom: '3mm' }}>{p.b}</div>
            <div style={{ fontSize: '8.5pt', color: C.magenta, fontWeight: 700, letterSpacing: '0.02em', paddingTop: '2mm', borderTop: `1px solid ${C.border}` }}>→ {p.d}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '8mm', padding: '6mm 7mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
          Months 10–12 · Scale
        </div>
        <div style={{ fontSize: '10.5pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
          Additional Qatar and GCC targets, local sales and implementation partnerships, first localisation modules released. <strong style={{ color: '#fff' }}>Deliverable:</strong> multiple active opportunities, one reference implementation, a defined 24-month plan.
        </div>
      </div>

      <div style={{ marginTop: '5mm', padding: '6mm 7mm', background: C.magenta, color: '#fff' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.22em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
          Months 13–24 · Growth stage
        </div>
        <div style={{ fontSize: '10.5pt', color: 'rgba(255,255,255,0.95)', lineHeight: 1.6 }}>
          Saudi entry with the ZATCA-integrated offering, five or more GCC sites, first Doha hires, halal and GSO modules live, Education City talent engagement. <strong style={{ color: '#fff' }}>Deliverable:</strong> a repeatable GCC engine running from Doha.
        </div>
      </div>
    </div>
    <PageFooter section="Milestones" />
  </Page>
);

// ==================== PAGE 12: Financials ====================

const FinancialsPage = () => {
  const bars = [
    { y: 'Year 1', tot: 185, arr: 154, sites: '4 sites', h: 22 },
    { y: 'Year 2', tot: 512, arr: 422, sites: '11 sites', h: 60 },
    { y: 'Year 3', tot: 943, arr: 768, sites: '20 sites', h: 100 },
  ];
  return (
    <Page pageNum={12} bg={C.cream}>
      <PageHeader pageNum={12} lot="Section 11 · Numbers" />
      <div style={{ paddingTop: '17mm' }}>
        <LotBadge n="11" label="Financials · conservative base case" />
        <H2 style={{ fontSize: '22pt', margin: '0 0 4mm' }}>
          Twenty sites. Three years.<br/>
          <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>No outside capital.</em>
        </H2>

        {/* Chart */}
        <div style={{ marginTop: '3mm', padding: '4mm 5mm', background: C.paper, border: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', gap: '5mm', alignItems: 'baseline', fontSize: '8pt', color: C.muted, marginBottom: '3mm', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 9, background: C.magenta, display: 'inline-block' }} />Total revenue</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 9, background: C.gold, display: 'inline-block' }} />ARR at year end</span>
            <span style={{ fontWeight: 500, letterSpacing: '0.06em', textTransform: 'none', color: C.muted }}>QAR (thousands)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10mm', height: '42mm', paddingLeft: '4mm', borderLeft: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
            {bars.map((b, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3mm', height: `${b.h}%`, width: '100%', justifyContent: 'center' }}>
                  <div style={{ width: '10mm', background: C.magenta, height: '100%', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)', fontSize: '10pt', fontWeight: 700, color: C.navy, whiteSpace: 'nowrap' }}>{b.tot}</div>
                  </div>
                  <div style={{ width: '10mm', background: C.gold, height: `${(b.arr / b.tot) * b.h}%`, position: 'relative' }}>
                    <div style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)', fontSize: '9pt', fontWeight: 700, color: C.slate, whiteSpace: 'nowrap' }}>{b.arr}</div>
                  </div>
                </div>
                <div style={{ marginTop: '3mm', fontSize: '10pt', fontWeight: 700, color: C.navy, letterSpacing: '-0.005em' }}>{b.y}</div>
                <div style={{ fontSize: '8.5pt', color: C.muted, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>{b.sites}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Table */}
        <table style={{ marginTop: '4mm', width: '100%', borderCollapse: 'collapse', fontSize: '8.5pt', color: C.slate }}>
          <thead>
            <tr style={{ background: C.navy, color: '#fff' }}>
              <th style={{ padding: '2mm 3mm', textAlign: 'left', fontWeight: 700, letterSpacing: '0.1em', fontSize: '7.5pt', textTransform: 'uppercase' }}>QAR</th>
              <th style={{ padding: '2mm 3mm', textAlign: 'right', fontWeight: 700, letterSpacing: '0.1em', fontSize: '7.5pt', textTransform: 'uppercase' }}>Year 1</th>
              <th style={{ padding: '2mm 3mm', textAlign: 'right', fontWeight: 700, letterSpacing: '0.1em', fontSize: '7.5pt', textTransform: 'uppercase' }}>Year 2</th>
              <th style={{ padding: '2mm 3mm', textAlign: 'right', fontWeight: 700, letterSpacing: '0.1em', fontSize: '7.5pt', textTransform: 'uppercase' }}>Year 3</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Active sites — Qatar and GCC', '1', '5', '11'],
              ['Active sites — Ireland and UK', '3', '6', '9'],
              ['Implementation revenue', '96,000', '224,000', '288,000'],
              ['Subscription revenue', '89,000', '288,000', '595,000'],
              ['Modules and services', '—', '—', '60,000'],
              ['Total revenue', '185,000', '512,000', '943,000', true],
              ['Core operating costs — committed', '150,000', '240,000', '380,000'],
              ['Expansion spend — released on validation', '110,000', '200,000', '320,000'],
              ['Net result', '(75,000)', '72,000', '243,000', true],
              ['Annual recurring revenue at year end', '154,000', '422,000', '768,000', true],
            ].map((r, i) => (
              <tr key={i} style={{ background: r[4] ? 'rgba(233,30,99,0.06)' : (i % 2 ? '#fff' : C.paper), borderBottom: `1px solid ${C.border}`, fontWeight: r[4] ? 700 : 400, color: r[4] ? C.navy : C.slate }}>
                <td style={{ padding: '1.8mm 3mm' }}>{r[0]}</td>
                <td style={{ padding: '1.8mm 3mm', textAlign: 'right' }}>{r[1]}</td>
                <td style={{ padding: '1.8mm 3mm', textAlign: 'right' }}>{r[2]}</td>
                <td style={{ padding: '1.8mm 3mm', textAlign: 'right' }}>{r[3]}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: '4mm', padding: '3.5mm 5mm', background: `rgba(201,169,97,0.14)`, borderLeft: `3px solid ${C.gold}`, fontSize: '8pt', color: C.slate, lineHeight: 1.55 }}>
          Built on an average implementation of QAR 32,000 and an average subscription of QAR 3,200 per site per month, with minimal churn. Costs are split deliberately: a lean committed base covering hosting, core development and market entry, and expansion spend released only against validated demand, so the downside is capped while the upside is funded. The Year 1 shortfall is met by the founder from established consulting revenue. Excluded from the base case, because they are realistic but not promised: multi-site producer groups, module upsell, channel volume and ecosystem grant support.
        </div>
      </div>
      <PageFooter section="Financials" />
    </Page>
  );
};

// ==================== PAGE 13: Risk ====================

const RiskPage = () => (
  <Page pageNum={13} bg={C.paper}>
    <PageHeader pageNum={13} lot="Section 12 · Risk" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="12" label="Risk" />
      <H2>
        What could go wrong,<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>said plainly.</em>
      </H2>
      <Body>
        A plan that lists no risks is a brochure. These are the six that matter, and what is already in place against each.
      </Body>

      <table style={{ marginTop: '5mm', width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt', color: C.slate }}>
        <thead>
          <tr style={{ background: C.navy, color: '#fff' }}>
            <th style={{ padding: '3.5mm 4mm', textAlign: 'left', fontWeight: 700, letterSpacing: '0.1em', fontSize: '8.5pt', textTransform: 'uppercase', width: '38%' }}>Risk</th>
            <th style={{ padding: '3.5mm 4mm', textAlign: 'left', fontWeight: 700, letterSpacing: '0.1em', fontSize: '8.5pt', textTransform: 'uppercase' }}>Mitigation</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Reliance on a single anchor customer', 'Ireland and UK pipeline converted in parallel with GCC entry. Contracted service levels. Every new reference reduces concentration.'],
            ['Long B2B sales cycles in a specialised market', 'Paid operational assessments as a low-friction entry product. Founder-led sales. Channel partnerships with consultants, accountants and technology providers.'],
            ['Regional localisation complexity', 'Phased roadmap on a modular core. Requirements validated with the first Qatar pilot before broad release.'],
            ['Choosing the wrong long-term IP structure', 'No structural change committed before validation. Professional tax and legal advice in both jurisdictions. Regional development proceeds from Doha regardless of the outcome.'],
            ['Founder dependency', 'Documented product and implementation methodology. Established contract development bench. Doha hires from the growth stage.'],
            ['ERP vendors moving down-market', 'Speed, vertical depth, SME pricing and the retailer-template documentation engine are structurally hard for large vendors to replicate in this segment.'],
          ].map((r, i) => (
            <tr key={i} style={{ background: i % 2 ? C.cream : '#fff', borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: '3.5mm 4mm', fontWeight: 700, color: C.navy, verticalAlign: 'top' }}>{r[0]}</td>
              <td style={{ padding: '3.5mm 4mm', lineHeight: 1.55 }}>{r[1]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: '9mm', padding: '7mm 8mm', background: C.navy, color: '#fff' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22pt', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.012em' }}>
          The product risk is behind us.<br/>
          <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>What remains is a distribution problem.</em>
        </div>
      </div>
    </div>
    <PageFooter section="Risk" />
  </Page>
);

// ==================== PAGE 14: The ask ====================

const AskPage = () => (
  <Page pageNum={14} bg={C.cream}>
    <PageHeader pageNum={14} lot="Section 13 · The ask" />
    <div style={{ paddingTop: '18mm' }}>
      <LotBadge n="13" label="The ask" />
      <H2>
        We are not asking<br/>
        <em style={{ fontStyle: 'italic', color: C.magenta, fontWeight: 500 }}>for survival funding.</em>
      </H2>
      <Body>
        The product exists, runs a factory every day and is funded by the founder. What the Digital Incubation Center offers that money cannot is a base, a registration path and a room full of the right people.
      </Body>

      <div style={{ marginTop: '6mm', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '5mm' }}>
        {[
          { h: 'A base in Doha', b: 'Incubation workspace as the company\u2019s operating base for the programme, with the founder present in Qatar throughout.' },
          { h: 'A registration path', b: 'The Center\u2019s first-year route from product to a registered Qatari business, providing the vehicle for Keystone\u2019s GCC operations, regional development and future IP structure.' },
          { h: 'Introductions', b: 'Ministry of Municipality food security programmes, food safety stakeholders, local producers across dairy, meat, aquaculture and prepared foods, Qatar Development Bank and Invest Qatar.' },
          { h: 'Standing', b: 'Visibility through the Center\u2019s platform and the national technology calendar. Enterprise sales run on credibility, and credibility here is granted locally.' },
        ].map((c, i) => (
          <div key={i} style={{ padding: '6mm 6mm', background: C.paper, borderLeft: `4px solid ${C.magenta}`, border: `1px solid ${C.border}` }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '15pt', color: C.navy, fontWeight: 700, letterSpacing: '-0.015em', marginBottom: '3mm' }}>{c.h}</div>
            <div style={{ fontSize: '9.5pt', color: C.slate, lineHeight: 1.6 }}>{c.b}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '10mm', padding: '10mm 10mm', background: C.navy, color: '#fff', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '32pt', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
          Give us the room.<br/>
          <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>We will build the Gulf business here.</em>
        </div>
      </div>
    </div>
    <PageFooter section="The ask" />
  </Page>
);

// ==================== PAGE 15: Founder ====================

const FounderPage = () => (
  <Page pageNum={15} bg={C.navy} noPad last>
    <div style={{ padding: '14mm 16mm', height: '100%', display: 'flex', flexDirection: 'column', color: '#fff', position: 'relative' }}>
      <PageHeader pageNum={15} dark lot="Section 14 · Founder" />

      <div style={{ paddingTop: '18mm', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: '9pt', letterSpacing: '0.28em', textTransform: 'uppercase', fontWeight: 700, color: C.gold, marginBottom: '4mm' }}>
          Section 14 · Founder
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '52mm 1fr', gap: '8mm', alignItems: 'start' }}>
          {/* Headshot */}
          <div>
            <img
              src="/team/asif.png"
              alt="Mohammed Asif Ali Khan, Founder, Keystone by CloudVoro"
              style={{
                width: '52mm', height: '62mm',
                objectFit: 'cover', objectPosition: 'center top',
                border: `2px solid ${C.gold}`,
                boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                display: 'block',
              }}
            />
            <div style={{ marginTop: '3mm', fontSize: '8pt', letterSpacing: '0.16em', textTransform: 'uppercase', color: C.gold, fontWeight: 700 }}>
              Founder & CEO
            </div>
          </div>
          <div>
            <h1 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '30pt', fontWeight: 700, color: '#fff',
              lineHeight: 1.08, letterSpacing: '-0.022em', margin: 0,
            }}>
              Mohammed Asif Ali Khan
            </h1>
            <div style={{ marginTop: '3mm', fontSize: '9pt', letterSpacing: '0.14em', textTransform: 'uppercase', color: C.gold, fontWeight: 700 }}>
              Founder &middot; Intellix IT Solutions Ltd &middot; Keystone by CloudVoro
            </div>
            <div style={{ marginTop: '4mm', fontSize: '10pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
              Technology entrepreneur and product builder. Ten years delivering enterprise systems and digital transformation across Ireland, Saudi Arabia and the UK. Director and owner of Intellix IT Solutions Ltd and creator of Keystone by CloudVoro.
            </div>
            <div style={{ marginTop: '3mm', fontSize: '10pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
              His Saudi experience includes direct and subcontracted technology delivery supporting government, defence, diplomatic and banking organisations, alongside ZATCA electronic invoicing implementations on Microsoft AX and SAP. Saudi professional relationships from that period continue today.
            </div>
            <div style={{ marginTop: '3mm', fontSize: '10pt', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
              MSc in IT Enabled Innovation, National University of Ireland Maynooth. Member of the British Computer Society. He will lead product strategy, Qatar market entry and commercial development personally, based in Doha through the incubation programme.
            </div>
          </div>
        </div>

        <div style={{ marginTop: '7mm', padding: '4mm 6mm', background: 'rgba(255,255,255,0.06)', borderLeft: `3px solid ${C.gold}` }}>
          <div style={{ fontSize: '8pt', letterSpacing: '0.24em', textTransform: 'uppercase', color: C.gold, fontWeight: 700, marginBottom: '3mm' }}>
            Organisations engaged &middot; direct or subcontracted
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '2.5mm 5mm', fontSize: '9pt', color: 'rgba(255,255,255,0.9)' }}>
            {[
              'Saudi Ministry of Defence',
              'Royal Saudi Air Force',
              'Indonesian Embassy, Riyadh',
              'QNB KSA',
              'Kaspersky',
              'Dunkin\u2019',
              'Cashel Blue Cheese',
              'Ballypatrick Auctions',
              'Boomerang.ie',
              'Baileys Hotel Cashel',
            ].map((o, i) => <div key={i}>· {o}</div>)}
          </div>
        </div>

        <div style={{ marginTop: '6mm', padding: '5mm 8mm', background: C.magenta, color: '#fff', textAlign: 'center' }}>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17pt', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.012em' }}>
            Built in a factory. Scaling from Qatar. <em style={{ fontStyle: 'italic', color: C.gold, fontWeight: 500 }}>Sold across the Gulf.</em>
          </div>
        </div>

        <div style={{ marginTop: '5mm', paddingTop: '4mm', borderTop: '1px solid rgba(255,255,255,0.15)', fontSize: '7pt', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, paddingBottom: '4mm' }}>
          Market and policy references: Qatar National Food Security Strategy 2030 (Ministry of Municipality, launched December 2024); Ministry of Municipality Food Security Department self-sufficiency figures, 2025; Qatar Science and Technology Park free zone framework (Qatar Foundation); USDA Foreign Agricultural Service GCC food market reporting. Financial figures are management estimates prepared for planning purposes. Establishment of the Qatar entity, and any future change to the group&rsquo;s corporate or intellectual property structure, would be made only with professional legal and tax advice in Ireland and Qatar.
        </div>
      </div>

      <PageFooter dark section="Founder" />
    </div>
  </Page>
);

// ==================== Root ====================

function BusinessPlanContent() {
  const [busy, setBusy] = React.useState(false);
  const download = async () => {
    setBusy(true);
    try { await downloadServerPDF('/brochure/bplan', 'Keystone-Business-Plan.pdf'); }
    catch (e) { alert(e?.message || 'PDF render failed'); }
    setBusy(false);
  };

  return (
    <div style={{ background: '#F1F5F9', minHeight: '100vh', padding: '24px 0' }} data-testid="brochure-bplan">
      <div style={{
        position: 'sticky', top: 12, zIndex: 10, maxWidth: '210mm', margin: '0 auto 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
        padding: '10px 16px', boxShadow: '0 4px 16px rgba(10,37,64,0.08)',
      }} className="brochure-actions no-print">
        <div style={{ fontSize: 13, color: C.slate }}>
          <strong style={{ color: C.navy }}>Business Plan</strong> · Doha · confidential
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={download}
            disabled={busy}
            data-testid="brochure-bplan-download-pdf"
            style={{ padding: '10px 18px', background: C.magenta, color: '#fff', border: 'none', borderRadius: 4, fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}
          >
            {busy ? 'Rendering PDF\u2026' : 'Download PDF'}
          </button>
        </div>
      </div>

      <CoverPage />
      <WhyPage />
      <PositionPage />
      <ProblemPage />
      <QatarPage />
      <ProductPage />
      <ProofPage />
      <QatarBasePage />
      <ExportPage />
      <ModelPage />
      <PlanPage />
      <FinancialsPage />
      <RiskPage />
      <AskPage />
      <FounderPage />

      <style>{`
        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          #root, #root > * { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .brochure-page {
            margin: 0 !important;
            box-shadow: none !important;
            border: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            overflow: hidden !important;
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .brochure-page:last-of-type {
            page-break-after: auto;
            break-after: auto;
          }
        }
        @page { size: A4; margin: 0; }
      `}</style>
    </div>
  );
}

export default function BrochureBusinessPlan() {
  return (
    <BrochureGate
      title="Business plan · Qatar · private."
      subtitle="This is a confidential Keystone business plan. Enter the access password to continue."
      buttonLabel="Unlock business plan"
      eyebrow="Keystone by CloudVoro · Confidential · Business plan"
    >
      <BusinessPlanContent />
    </BrochureGate>
  );
}
